import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PortfolioApi } from 'src/app/_shared/api/portfolio.api';
import { ProspektrApi } from 'src/app/_shared/api/prospektr.api';
import { PublicRecordApi as _PublicRecordApi } from 'src/app/_shared/api/public-record.api';
import { PortfolioState } from '../state/portfolio.state';

@Injectable({
  providedIn: 'root',
})
export class PortfolioService {
  constructor(
    private portfolioApi: PortfolioApi,
    private portfolioState: PortfolioState,
    private snackBar: MatSnackBar,
    private _publicRecordApi: _PublicRecordApi,
    public router: Router,
    private prospektrApi: ProspektrApi
  ) {}

  getUserPortfolioDetails(userId) {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.portfolioApi
        .getUserPortfolioDetails(userId)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            if (res.data) {
              this.portfolioState.portfolioContactDetailsValue = res.data;
            } else {
              this.portfolioState.portfolioContactDetailsValue = null;
            }
          },

          (err: any) => {
            this.router.navigate(['workspace']);
            this.openSnackBar(err.error.message, 'snackbar-error');
            this.portfolioState.portfolioContactDetailsValue = null;
          }
        );
    });
  }

  getMlsImageInfo(mlsBoard) {
    const params = {
      params: mlsBoard,
    };
    this._publicRecordApi.getMlsImageDetails(params).subscribe((res: any) => {
      if (res && res.data) {
        let mlsImageList = this.portfolioState.mlsImageValue;
        this.portfolioState.mlsImageValue = [
          ...mlsImageList,
          {
            boardName: mlsBoard,
            imageUrl: res?.data?.logoUrl,
          },
        ];
      }
    });
  }

  sendContactInfo(userId, contactDetails): Observable<{ isLoading: boolean }> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.portfolioApi
        .sendContactInfo(userId, contactDetails)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            this.openSnackBar(
              res.message || "Notified Successfully. Soon we'll be in touch with you.",
              'snackbar-success'
            );
          },
          (err) => {
            this.openSnackBar(err.error.message || 'Something went wrong. Please try again.', 'snackbar-error');
          }
        );
    });
  }

  private openSnackBar(message, style) {
    if (message) {
      this.snackBar.open(message, '', {
        duration: 3000,
        panelClass: [style ? style : ''],
        verticalPosition: 'top',
      });
    }
  }

  getUserPortfolioInfo() {
    this.prospektrApi.getUserPortfolio().subscribe((res) => {
      if (res.data) {
        this.portfolioState.userPortfolioValue = res.data;
      } else {
        this.portfolioState.userPortfolioValue = {};
      }
    });
  }

  uploadFile(url: string, file: File): Observable<any> {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .uploadImage(file)
        .pipe(
          finalize(() => {
            observer.complete();
          })
        )
        .subscribe(
          (res) => {
            observer.next({ isLoading: false, response: res });
          },
          (err) => {
            observer.next({ status: 'Failed' });
          }
        );
    });
  }

  addLicense(value) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    if (userPortfolioValue.hasOwnProperty('licenseList')) {
      userPortfolioValue.licenseList.push(value);
    } else {
      userPortfolioValue.licenseList = [value];
    }
    userPortfolioValue.licenseList = userPortfolioValue.licenseList.sort((a, b) => b.yearIssued - a.yearIssued);
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  deleteLicense(user) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    let filteredLicenseList = userPortfolioValue.licenseList.filter(function (item) {
      return item.number !== user.number;
    });
    userPortfolioValue.licenseList = filteredLicenseList;
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  addAssociation(value) {
    let response = {
      state: value.associationsState,
      name: value.associationsList == 'other' ? value.associationsName : value.associationsList,
      since: value.associationsYear,
    };
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    if (userPortfolioValue.hasOwnProperty('associationList')) {
      userPortfolioValue.associationList.push(response);
    } else {
      userPortfolioValue.associationList = [response];
    }
    userPortfolioValue.associationList = userPortfolioValue.associationList.sort((a, b) => b.since - a.since);
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  deleteAssociation(user) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    let filter = userPortfolioValue.associationList.filter(function (item) {
      return item.name !== user.name;
    });
    userPortfolioValue.associationList = filter;
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  addExperience(value) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    if (userPortfolioValue.hasOwnProperty('experienceList')) {
      userPortfolioValue.experienceList.push(value);
    } else {
      userPortfolioValue.experienceList = [value];
    }
    userPortfolioValue.experienceList.reverse();
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  deleteExperience(index) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    if (userPortfolioValue.experienceList[index]) {
      userPortfolioValue.experienceList.splice(index, 1);
    }
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  addSummary(value) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    userPortfolioValue.summary = value;
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  uploadBgImg(value): Observable<{ isLoading: boolean }> {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    userPortfolioValue.backgroundImage = value;
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.updatePortfolio()
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(({ isLoading }) => {
          observer.next({ isLoading });
        });
    });
  }

  uploadLogo(value) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    userPortfolioValue.logo = value;
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  displaySection(value) {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    userPortfolioValue.displaySection = value;
    this.portfolioState.userPortfolioValue = userPortfolioValue;
    this.updatePortfolio().subscribe();
  }

  getAssociationName(params) {
    let state = {
      state: params,
    };
    return new Observable((observer) => {
      let associationName = [];
      observer.next({ isLoading: true });
      this.prospektrApi
        .getAssociation(state)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: true, associationNameList: associationName });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            if (res) {
              associationName = res.data;
            }
          },
          (err) => {
            observer.next({ status: 'Failed' });
          }
        );
    });
  }

  updatePortfolio(): Observable<{ isLoading: boolean }> {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    let params = {
      summary: userPortfolioValue.summary,
      associationList: userPortfolioValue.associationList,
      licenseList: userPortfolioValue.licenseList,
      experienceList: userPortfolioValue.experienceList,
      backgroundImage: userPortfolioValue.backgroundImage,
      displaySection: userPortfolioValue.displaySection,
      logo: userPortfolioValue.logo,
    };
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .updatePortfolio(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe((res) => {
          if (res) this.openSnackbar('Portfolio Updated successfully', 'snackbar-success');
        });
    });
  }

  updateImage(): Observable<{ isLoading: boolean }> {
    let userPortfolioValue: any = this.portfolioState.userPortfolioValue;
    let params = {
      summary: userPortfolioValue.summary,
      associationList: userPortfolioValue.associationList,
      licenseList: userPortfolioValue.licenseList,
      experienceList: userPortfolioValue.experienceList,
      backgroundImage: userPortfolioValue.backgroundImage,
      displaySection: userPortfolioValue.displaySection,
      logo: userPortfolioValue.logo,
    };
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.prospektrApi
        .updatePortfolio(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe((res) => {
          if (res) this.openSnackbar('Portfolio Updated successfully', 'snackbar-success');
        });
    });
  }

  getSuggestedBannerImages() {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this._publicRecordApi
        .getPortfolioBannerImages()
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            if (res.data) {
              this.portfolioState.portfolioBannerImageValue = res.data;
            } else {
              this.portfolioState.portfolioBannerImageValue = null;
            }
          },

          (err: any) => {
            this.openSnackBar(err.error.message, 'snackbar-error');
            this.portfolioState.portfolioBannerImageValue = null;
          }
        );
    });
  }

  getSuggestedProfileImages() {
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this._publicRecordApi
        .getPortfolioProfileImages()
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            if (res.data) {
              this.portfolioState.portfolioProfileImageValue = res.data;
            } else {
              this.portfolioState.portfolioProfileImageValue = null;
            }
          },

          (err: any) => {
            this.openSnackBar(err.error.message, 'snackbar-error');
            this.portfolioState.portfolioProfileImageValue = null;
          }
        );
    });
  }

  openSnackbar(message, style) {
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [style],
      verticalPosition: 'top',
    });
  }
}
